/**
 * @fileoverview AR-670 — Classroom block of the teacher's deliverable drawer:
 * assignment state (last synced + open link), a "Refresh from Google
 * Classroom" action and a warning when the student has no Classroom mapping
 * (prototype: components/Activities/StudentDeliverableDrawerBadge.tsx). The
 * import endpoint is project-wide (backend has no per-activity import yet) —
 * the drawer reloads afterward to pick up this task's changes.
 */

import { useTranslation } from 'react-i18next'
import { Alert, Button, Group, Stack } from '@mantine/core'
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react'

import { notifications } from 'context'
import {
  ClassroomAssignmentState,
  isClassroomPortError,
  useClassroomIntegrationEnabled,
  useClassroomSync,
  useProjectClassroomLink,
} from 'integrations/classroom'
import { ClassroomAssignmentStatus } from './ClassroomAssignmentStatus'

/** Known 422s of sync_submissions — specific copy instead of the generic one. */
const REFRESH_ERROR_KEYS: Partial<Record<string, string>> = {
  // Sync runs on the connection RECORDED on the link (possibly a colleague's),
  // so the copy must not say "reconnect YOUR account".
  needs_reconnection: 'classroomSyncLinkNeedsReconnection',
  not_connected: 'projectClassroomConnectYourAccount',
  // The tutor who linked disconnected — relinking, not connecting, fixes it.
  connection_gone: 'projectClassroomConnectionGone',
  not_linked: 'projectClassroomNotLinked',
  sync_disabled: 'projectClassroomSyncDisabled',
}

interface ClassroomDeliverableSyncSectionProps {
  readonly projectId: string
  readonly studentId: string
  readonly state: ClassroomAssignmentState
  /** Re-fetch the drawer data after an import brought new submissions. */
  readonly onSynced: () => void
}

export function ClassroomDeliverableSyncSection({
  projectId,
  studentId,
  state,
  onSynced,
}: ClassroomDeliverableSyncSectionProps) {
  const { enabled } = useClassroomIntegrationEnabled()

  // Flag off: keep the read-only status (data already shipped with the task)
  // and skip the roster fetch + refresh action entirely.
  if (!enabled) return <ClassroomAssignmentStatus state={state} />

  return (
    <ClassroomSyncSectionInner projectId={projectId} studentId={studentId} state={state} onSynced={onSynced} />
  )
}

function ClassroomSyncSectionInner({
  projectId,
  studentId,
  state,
  onSynced,
}: ClassroomDeliverableSyncSectionProps) {
  const { t } = useTranslation()
  const sync = useClassroomSync()
  const link = useProjectClassroomLink(projectId)
  const tr = (key: string) => t(`classroom.${key}`)

  // Mapping known only after the link state lands; `undefined` = still unknown
  // (no warning, no blocked button — never flash a false accusation). The
  // read-only roster only PERSISTS matched students (always "enrolled"), so
  // presence in the list IS the mapping.
  const roster = link.state?.rosterStudents
  const studentMapped = roster
    ? roster.some(student => student.studentId === studentId)
    : undefined

  const handleRefresh = async () => {
    try {
      const result = await sync.syncSubmissions(projectId)
      // `updated` counts re-submissions merged into an existing delivery —
      // their attachments changed too, so they reload the drawer like imports.
      const changed = result.reduce(
        (total, assignment) => total + assignment.summary.imported + assignment.summary.updated,
        0,
      )
      notifications.show({
        color: changed > 0 ? 'green' : 'blue',
        message:
          changed > 0
            ? t('classroom.classroomSyncSubmissionsImported', { count: changed })
            : tr('classroomSyncSubmissionsNothingNew'),
      })
      if (changed > 0) onSynced()
    } catch (error) {
      const messageKey = isClassroomPortError(error) ? REFRESH_ERROR_KEYS[error.code] : undefined
      notifications.show({
        color: messageKey ? 'yellow' : 'red',
        message: tr(messageKey || 'classroomSyncSubmissionsFailed'),
      })
    }
  }

  return (
    <Stack spacing='xs'>
      <ClassroomAssignmentStatus state={state} />

      {studentMapped === false && (
        <Alert color='yellow' variant='light' icon={<IconAlertTriangle size={16} />}>
          {tr('classroomSyncMissingStudentLink')}
        </Alert>
      )}

      {state.status === 'created' && studentMapped !== false && (
        <Group>
          <Button
            size='xs'
            variant='outline'
            leftIcon={<IconRefresh size={14} />}
            loading={sync.syncing}
            onClick={handleRefresh}
          >
            {tr('classroomSyncRefreshButton')}
          </Button>
        </Group>
      )}
    </Stack>
  )
}
